import { Box } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { HiOutlineClipboardDocumentCheck, HiOutlineAcademicCap, HiOutlineChartBar } from "react-icons/hi2";
import {
    Field,
    FormInput,
    FormSelect,
    FormSwitch,
    SwitchGroup,
    Grid2,
    Grid3,
    CollapsibleSection,
    ItemCard,
    ItemHeader,
    AddButton,
    AdditionalInfoField,
    isFieldInvalid,
    isFieldWarning,
} from "../ui";
import { ScoringConfigPanel } from "@/scoringUi";
import { useScoringActive } from "@/auth/ScoringContext";
import { buildScoringLabels } from "../scoringLabels";
import {
    useJobOfferEnums,
    createEmptyRequiredDocument,
    createEmptySkill,
    createEmptyExperienceRequirement,
} from "@/models/jobOffer";

const Step4Requirements = ({ formik, warningErrors, visited, forceOpen }) => {
    const scoringActive = useScoringActive();
    const { t } = useTranslation("jobOffer");
    const scoringLabels = buildScoringLabels(t);
    const { DOCUMENT_TYPE, REQUIREMENT_STATUS, CRITICALITY, SKILL_CATEGORY, RATING_SCALE, CONFIRMATION_TYPE } =
        useJobOfferEnums();
    const { values, setFieldValue } = formik;
    const { requiredDocuments, skills, experienceRequirements } = values;
    const invalid = (path) => isFieldInvalid(formik, path);
    const warn = (path) => isFieldWarning(warningErrors, visited, path);
    const sectionInfo = values.sectionAdditionalInfo ?? {};
    const setSectionInfo = (key, val) => setFieldValue(`sectionAdditionalInfo.${key}`, val);

    const updateDoc = (i, updates) =>
        setFieldValue(
            "requiredDocuments",
            requiredDocuments.map((d, idx) => (idx === i ? { ...d, ...updates } : d))
        );
    const removeDoc = (i) => setFieldValue("requiredDocuments", requiredDocuments.filter((_, idx) => idx !== i));

    const updateSkill = (i, updates) =>
        setFieldValue(
            "skills",
            skills.map((s, idx) => (idx === i ? { ...s, ...updates } : s))
        );
    const removeSkill = (i) => setFieldValue("skills", skills.filter((_, idx) => idx !== i));

    const updateExperience = (i, updates) =>
        setFieldValue(
            "experienceRequirements",
            experienceRequirements.map((e, idx) => (idx === i ? { ...e, ...updates } : e))
        );
    const removeExperience = (i) => setFieldValue("experienceRequirements", experienceRequirements.filter((_, idx) => idx !== i));

    return (
        <Box display="flex" flexDirection="column" gap={6}>
            {/* Section 9: Documents */}
            <CollapsibleSection
                number={9}
                title={t("step4.documents.title")}
                subtitle={t("step4.documents.subtitle")}
                icon={HiOutlineClipboardDocumentCheck}
                defaultOpen
                forceOpen={forceOpen}
            >
                <Box display="flex" flexDirection="column" gap={3}>
                    {requiredDocuments.map((doc, i) => (
                        <ItemCard key={i}>
                            <ItemHeader
                                title={t("step4.documents.itemTitle")}
                                index={i}
                                badge={doc.name || DOCUMENT_TYPE.find((opt) => opt.value === doc.documentType)?.label}
                                onRemove={() => removeDoc(i)}
                            />
                            <Box display="flex" flexDirection="column" gap={3}>
                                <Field label={t("step4.documents.name")} required invalid={invalid(`requiredDocuments.${i}.name`)} warning={warn(`requiredDocuments.${i}.name`)}>
                                    <FormInput
                                        value={doc.name}
                                        onChange={(e) => updateDoc(i, { name: e.target.value })}
                                        placeholder={t("step4.documents.namePlaceholder")}
                                    />
                                </Field>
                                <Grid3>
                                    <Field label={t("step4.shared.status")} required invalid={invalid(`requiredDocuments.${i}.status`)} warning={warn(`requiredDocuments.${i}.status`)}>
                                        <FormSelect value={doc.status} onChange={(v) => updateDoc(i, { status: v })} placeholder={t("step4.shared.statusPlaceholder")} options={REQUIREMENT_STATUS} />
                                    </Field>
                                    <Field label={t("step4.shared.criticality")}>
                                        <FormSelect value={doc.criticality} onChange={(v) => updateDoc(i, { criticality: v })} placeholder={t("step4.shared.criticalityPlaceholder")} options={CRITICALITY} />
                                    </Field>
                                </Grid3>
                                <SwitchGroup>
                                    <FormSwitch label={t("step4.documents.requiresUpload")} checked={doc.requiresUpload} onChange={(v) => updateDoc(i, { requiresUpload: v })} />
                                    <FormSwitch label={t("step4.documents.mustBeCurrent")} checked={doc.mustBeCurrent} onChange={(v) => updateDoc(i, { mustBeCurrent: v })} />
                                </SwitchGroup>
                                <AdditionalInfoField value={doc.additionalInfo} onChange={(e) => updateDoc(i, { additionalInfo: e.target.value })} />
                                {scoringActive && (
                                    <ScoringConfigPanel config={doc.scoring} onChange={(scoring) => updateDoc(i, { scoring })} labels={scoringLabels} />
                                )}
                            </Box>
                        </ItemCard>
                    ))}
                    <AddButton label={t("step4.documents.add")} onClick={() => setFieldValue("requiredDocuments", [...requiredDocuments, createEmptyRequiredDocument()])} />
                </Box>
            </CollapsibleSection>

            {/* Section 10: Skills */}
            <CollapsibleSection
                number={10}
                title={t("step4.skills.title")}
                subtitle={t("step4.skills.subtitle")}
                icon={HiOutlineAcademicCap}
                forceOpen={forceOpen}
            >
                <Box display="flex" flexDirection="column" gap={3}>
                    {skills.map((skill, i) => (
                        <ItemCard key={i}>
                            <ItemHeader title={t("step4.skills.itemTitle")} index={i} badge={skill.name || undefined} onRemove={() => removeSkill(i)} />
                            <Box display="flex" flexDirection="column" gap={3}>
                                <Field label={t("step4.skills.name")} required invalid={invalid(`skills.${i}.name`)} warning={warn(`skills.${i}.name`)}>
                                    <FormInput
                                        value={skill.name}
                                        onChange={(e) => updateSkill(i, { name: e.target.value })}
                                        placeholder={t("step4.skills.namePlaceholder")}
                                    />
                                </Field>
                                <Grid3>
                                    <Field label={t("step4.skills.category")} required invalid={invalid(`skills.${i}.category`)} warning={warn(`skills.${i}.category`)}>
                                        <FormSelect value={skill.category} onChange={(v) => updateSkill(i, { category: v })} placeholder={t("step4.skills.categoryPlaceholder")} options={SKILL_CATEGORY} />
                                    </Field>
                                    <Field label={t("step4.shared.status")} required invalid={invalid(`skills.${i}.status`)} warning={warn(`skills.${i}.status`)}>
                                        <FormSelect value={skill.status} onChange={(v) => updateSkill(i, { status: v })} placeholder={t("step4.shared.statusPlaceholder")} options={REQUIREMENT_STATUS} />
                                    </Field>
                                    <Field label={t("step4.shared.criticality")}>
                                        <FormSelect value={skill.criticality} onChange={(v) => updateSkill(i, { criticality: v })} placeholder={t("step4.shared.criticalityPlaceholder")} options={CRITICALITY} />
                                    </Field>
                                </Grid3>
                                <Grid2>
                                    <Field label={t("step4.skills.ratingScale")}>
                                        <FormSelect value={skill.ratingScale} onChange={(v) => updateSkill(i, { ratingScale: v })} placeholder={t("step4.skills.ratingScalePlaceholder")} options={RATING_SCALE} />
                                    </Field>
                                    <Field label={t("step4.skills.minLevel")}>
                                        <FormInput
                                            value={skill.minLevel ?? ""}
                                            onChange={(e) => updateSkill(i, { minLevel: e.target.value === "" ? null : e.target.value })}
                                            placeholder={t("step4.skills.minLevelPlaceholder")}
                                        />
                                    </Field>
                                </Grid2>
                                <SwitchGroup>
                                    <FormSwitch label={t("step4.skills.aiAsksLevel")} checked={skill.aiAsksLevel} onChange={(v) => updateSkill(i, { aiAsksLevel: v })} />
                                    <FormSwitch label={t("step4.skills.requiresConfirmation")} checked={skill.requiresConfirmation} onChange={(v) => updateSkill(i, { requiresConfirmation: v })} />
                                </SwitchGroup>
                                {skill.requiresConfirmation && (
                                    <Field label={t("step4.skills.confirmationType")} required invalid={invalid(`skills.${i}.confirmationType`)} warning={warn(`skills.${i}.confirmationType`)}>
                                        <FormSelect value={skill.confirmationType} onChange={(v) => updateSkill(i, { confirmationType: v })} placeholder={t("step4.skills.confirmationTypePlaceholder")} options={CONFIRMATION_TYPE} />
                                    </Field>
                                )}
                                <AdditionalInfoField value={skill.additionalInfo} onChange={(e) => updateSkill(i, { additionalInfo: e.target.value })} />
                                {scoringActive && (
                                    <ScoringConfigPanel config={skill.scoring} onChange={(scoring) => updateSkill(i, { scoring })} labels={scoringLabels} />
                                )}
                            </Box>
                        </ItemCard>
                    ))}
                    <AddButton label={t("step4.skills.add")} onClick={() => setFieldValue("skills", [...skills, createEmptySkill()])} />
                </Box>
            </CollapsibleSection>

            {/* Section 11: Experience */}
            <CollapsibleSection
                number={11}
                title={t("step4.experience.title")}
                subtitle={t("step4.experience.subtitle")}
                icon={HiOutlineChartBar}
                forceOpen={forceOpen}
            >
                <Box display="flex" flexDirection="column" gap={3}>
                    {experienceRequirements.map((exp, i) => (
                        <ItemCard key={i}>
                            <ItemHeader title={t("step4.experience.itemTitle")} index={i} badge={exp.area || undefined} onRemove={() => removeExperience(i)} />
                            <Box display="flex" flexDirection="column" gap={3}>
                                <Field label={t("step4.experience.area")} required invalid={invalid(`experienceRequirements.${i}.area`)} warning={warn(`experienceRequirements.${i}.area`)}>
                                    <FormInput
                                        value={exp.area}
                                        onChange={(e) => updateExperience(i, { area: e.target.value })}
                                        placeholder={t("step4.experience.areaPlaceholder")}
                                    />
                                </Field>
                                <Grid3>
                                    <Field label={t("step4.experience.minYears")}>
                                        <FormInput
                                            type="number"
                                            min="0"
                                            value={exp.minYears ?? ""}
                                            onChange={(e) => updateExperience(i, { minYears: e.target.value === "" ? null : Number(e.target.value) })}
                                            placeholder={t("step4.experience.minYearsPlaceholder")}
                                        />
                                    </Field>
                                    <Field label={t("step4.shared.status")} required invalid={invalid(`experienceRequirements.${i}.status`)} warning={warn(`experienceRequirements.${i}.status`)}>
                                        <FormSelect value={exp.status} onChange={(v) => updateExperience(i, { status: v })} placeholder={t("step4.shared.statusPlaceholder")} options={REQUIREMENT_STATUS} />
                                    </Field>
                                </Grid3>
                                {scoringActive && (
                                    <ScoringConfigPanel config={exp.scoring} onChange={(scoring) => updateExperience(i, { scoring })} labels={scoringLabels} />
                                )}
                            </Box>
                        </ItemCard>
                    ))}
                    <AddButton label={t("step4.experience.add")} onClick={() => setFieldValue("experienceRequirements", [...experienceRequirements, createEmptyExperienceRequirement()])} />
                    <AdditionalInfoField value={sectionInfo.experience} onChange={(e) => setSectionInfo("experience", e.target.value)} />
                </Box>
            </CollapsibleSection>
        </Box>
    );
};

export default Step4Requirements;
